/**
 * InvoiceEditor Component
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { ToggleSwitch } from "../components/shared/ToggleSwitch";
import { openAuthenticatedPdf } from '../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Utility functions
const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getProvinceTaxSummary = (province) => {
  const rates = {
    'AB': { rate: 5, components: [{ name: 'GST', rate: 5 }] },
    'BC': { rate: 12, components: [{ name: 'GST', rate: 5 }, { name: 'PST', rate: 7 }] },
    'MB': { rate: 12, components: [{ name: 'GST', rate: 5 }, { name: 'PST', rate: 7 }] },
    'NB': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'NL': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'NS': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'NT': { rate: 5, components: [{ name: 'GST', rate: 5 }] },
    'NU': { rate: 5, components: [{ name: 'GST', rate: 5 }] },
    'ON': { rate: 13, components: [{ name: 'HST', rate: 13 }] },
    'PE': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'QC': { rate: 14.975, components: [{ name: 'GST', rate: 5 }, { name: 'QST', rate: 9.975 }] },
    'SK': { rate: 11, components: [{ name: 'GST', rate: 5 }, { name: 'PST', rate: 6 }] },
    'YT': { rate: 5, components: [{ name: 'GST', rate: 5 }] }
  };
  return rates[province] || rates['ON'];
};

// HtmlPreviewModal component (inline for self-containment)
const HtmlPreviewModal = ({ title, url, onClose, testId }) => {
  if (!url) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start sm:items-center justify-center z-50 p-2 pt-12 sm:p-4 sm:pt-4 overflow-y-auto" data-testid={testId || 'html-preview-modal'}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[85vh] sm:h-[86vh] flex flex-col my-auto sm:my-0">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800 truncate" data-testid={`${testId || 'html-preview-modal'}-title`}>{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1" data-testid={`${testId || 'html-preview-modal'}-close`}>✕</button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto">
          <iframe src={url} title={title} className="w-full h-full border-0" style={{ minHeight: '100%' }} key={url} data-testid={`${testId || 'html-preview-modal'}-iframe`} />
        </div>
      </div>
    </div>
  );
};

const InvoiceEditor = ({ invoice, onBack, onSave, onDuplicate, onRefresh, currentUser }) => {
  const [formData, setFormData] = useState(invoice || {
    client_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    client_city: '',
    client_province: 'ON',
    client_postal_code: '',
    crm_lead_id: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    po_number: '',
    status: 'DRAFT',
    line_items: [],
    subtotal: 0,
    markup_type: 'percent',
    markup_percentage: 0,
    markup_amount: 0,
    discount_type: 'percent',
    discount_percentage: 0,
    discount_amount: 0,
    tax_rate: 13.0,
    tax_rate_manual_override: false,
    tax_amount: 0,
    total: 0,
    notes: '',
    private_notes: '',
    payments: [],
    payment_schedule: [],
    payment_schedule_type: 'percent',
    requested_amount: null,
    amount_paid: 0,
    balance_due: 0,
    total_cc_fees_paid: 0,
    cc_fee_enabled: false,
    cc_fee_type: 'percent',
    cc_fee_value: 0,
    cc_fee_amount: 0,
    created_by: ''
  });

  const [libraryItems, setLibraryItems] = useState([]);
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [changeOrderMode, setChangeOrderMode] = useState(false);
  const [salesSettings, setSalesSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showInvoiceActionsMenu, setShowInvoiceActionsMenu] = useState(false);
  const [invoiceAutoSaveState, setInvoiceAutoSaveState] = useState('idle');
  const invoiceAutoSaveTimerRef = useRef(null);
  const invoiceSkipAutoSaveRef = useRef(false);
  const invoiceDidInitAutoSaveRef = useRef(false);
  const invoiceAutoSaveBlockUntilRef = useRef(0);
  const invoiceInitialLoadRef = useRef(null);
  const formDataRef = useRef(formData);
  
  const [showSendModal, setShowSendModal] = useState(false);
  const [showInvoiceSignatureWarning, setShowInvoiceSignatureWarning] = useState(false);
  const [sendForm, setSendForm] = useState({ recipient_email: '', message: '', request_type: 'full', request_value: '', attach_pdf: false, cc_admin: true });
  const [showChangeOrderSendModal, setShowChangeOrderSendModal] = useState(false);
  const [changeOrderSendForm, setChangeOrderSendForm] = useState({ recipient_email: '', message: 'Here is the change order for your approval:' });
  const [changeOrderToSend, setChangeOrderToSend] = useState(null);
  const [changeOrderSending, setChangeOrderSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'Manual',
    paid_date: new Date().toISOString().split('T')[0],
    notes: '',
    schedule_entry_id: '',
    payFullAmount: false,
    cc_fee_enabled: false,
    cc_fee_type: 'percent',
    cc_fee_value: 0
  });
  const [expandedInvoiceItemId, setExpandedInvoiceItemId] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [editPaymentId, setEditPaymentId] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({
    amount: '',
    method: 'Manual',
    paid_date: '',
    notes: '',
    schedule_entry_id: '',
    cc_fee_enabled: false,
    cc_fee_type: 'percent',
    cc_fee_value: 0
  });

  const [previewInvoiceToken, setPreviewInvoiceToken] = useState('');
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('');
  const [previewInvoiceNonce, setPreviewInvoiceNonce] = useState(0);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerKey, setSelectedCustomerKey] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const scheduleSnapshotRef = useRef(null);

  const publicLink = formData?.public_token ? `${window.location.origin}/invoice/${formData.public_token}` : '';
  const getInvoiceId = () => formData.id || invoice?.id;
  const isAdmin = currentUser?.rawRole === 'admin';

  const applyInvoiceUpdate = (nextInvoice, options = {}) => {
    if (!nextInvoice) return;
    const { refreshPreview = true } = options;
    invoiceSkipAutoSaveRef.current = true;
    if (invoiceAutoSaveTimerRef.current) {
      clearTimeout(invoiceAutoSaveTimerRef.current);
    }
    setFormData(nextInvoice);
    onRefresh?.(nextInvoice);
    if (refreshPreview) {
      setPreviewInvoiceNonce(Date.now());
    }
  };

  const applyInvoiceUpdateFromResponse = (response, options = {}) => {
    const nextInvoice = response?.data?.invoice || response?.data;
    if (nextInvoice?.id) {
      applyInvoiceUpdate(nextInvoice, options);
    }
    return nextInvoice;
  };

  useEffect(() => {
    const loadItems = async () => {
      try {
        const [itemsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/sales/items`),
          axios.get(`${API}/sales/settings`)
        ]);
        setLibraryItems(itemsRes.data);
        setSalesSettings(settingsRes.data);
      } catch (error) {
        console.error('Error loading items:', error);
      }
    };
    loadItems();
  }, []);

  useEffect(() => {
    const loadCustomerOptions = async () => {
      try {
        const [leadsRes, intakesRes, clientsRes] = await Promise.allSettled([
          axios.get(`${API}/crm/leads`),
          axios.get(`${API}/intakes`),
          axios.get(`${API}/sales/clients`)
        ]);
        const leadsData = leadsRes.status === 'fulfilled' ? (leadsRes.value.data || []) : [];
        const intakesData = intakesRes.status === 'fulfilled' ? (intakesRes.value.data || []) : [];
        const clientsData = clientsRes.status === 'fulfilled' ? (clientsRes.value.data || []) : [];
        const leadOptions = leadsData.map((lead) => ({
          key: `crm:${lead.id}`,
          label: `${lead.name || lead.email || 'CRM Lead'}${lead.email ? ` • ${lead.email}` : ''}`,
          source: 'CRM',
          data: lead
        }));
        const intakeOptions = intakesData.map((intake) => ({
          key: `intake:${intake.id}`,
          label: `${intake.name || intake.email || 'Intake'}${intake.email ? ` • ${intake.email}` : ''}`,
          source: 'Intake',
          data: intake
        }));
        const clientOptions = clientsData.map((client) => ({
          key: `client:${client.id}`,
          label: `${client.name || client.email || 'Client'}${client.email ? ` • ${client.email}` : ''}`,
          source: 'Client',
          data: client
        }));
        // Deduplicate: prefer Client > CRM > Intake, keyed by email or name
        const all = [...clientOptions, ...leadOptions, ...intakeOptions];
        const seen = new Set();
        const unique = all.filter(opt => {
          const dedupKey = (opt.data.email || opt.data.name || '').toLowerCase().trim();
          if (!dedupKey || seen.has(dedupKey)) return false;
          seen.add(dedupKey);
          return true;
        });
        setCustomerOptions(unique);
      } catch (error) {
        console.error('Error loading customer options:', error);
      }
    };
    loadCustomerOptions();
  }, []);

  useEffect(() => {
    if (!selectedCustomerKey && formData.crm_lead_id && customerOptions.length) {
      const match = customerOptions.find((option) => option.source === 'CRM' && option.data.id === formData.crm_lead_id);
      if (match) {
        setSelectedCustomerKey(match.key);
      }
    }
  }, [customerOptions, formData.crm_lead_id, selectedCustomerKey]);

  useEffect(() => {
    const schedule = formData.payment_schedule || [];
    if (!schedule.length) {
      if (formData.requested_amount !== null && formData.requested_amount !== undefined) {
        setFormData(prev => ({ ...prev, requested_amount: null }));
      }
      return;
    }
    const balanceValue = Number(formData.balance_due ?? 0);
    // If balance is 0 or less, no payment is due
    if (balanceValue <= 0) {
      if (formData.requested_amount !== 0) {
        setFormData(prev => ({ ...prev, requested_amount: 0 }));
      }
      return;
    }
    const totalValue = Number(formData.total || 0);
    const schedType = formData.payment_schedule_type || 'percent';

    // Find next unpaid entry and use its amount at current rates, capped at balance
    const nextItem = schedule.find(item => !item.paid);
    if (!nextItem) {
      if (formData.requested_amount !== null && formData.requested_amount !== undefined) {
        setFormData(prev => ({ ...prev, requested_amount: null }));
      }
      return;
    }
    let entryAmount = parseFloat(nextItem.amount || 0);
    if (Number.isNaN(entryAmount)) entryAmount = 0;
    if (schedType === 'percent') {
      entryAmount = (totalValue * entryAmount) / 100;
    }
    // Subtract what's already been paid on this entry
    const paidSoFar = parseFloat(nextItem.paid_amount || 0);
    entryAmount = Math.max(0, entryAmount - paidSoFar);
    // Cap at balance_due
    const rounded = Math.round(Math.max(0, Math.min(entryAmount, balanceValue)) * 100) / 100;
    if (rounded !== formData.requested_amount) {
      setFormData(prev => ({ ...prev, requested_amount: rounded }));
    }
  }, [formData.payment_schedule, formData.payment_schedule_type, formData.total, formData.balance_due, formData.amount_paid]);

  useEffect(() => {
    if (!formData.id) return;
    const key = `invoice-line-item-expanded-${formData.id}`;
    const stored = localStorage.getItem(key);
    if (stored && formData.line_items.some(item => item.id === stored)) {
      setExpandedInvoiceItemId(stored);
    } else {
      setExpandedInvoiceItemId(null);
    }
  }, [formData.id]);

  const persistExpandedInvoiceItem = (itemId) => {
    if (!formData.id) return;
    const key = `invoice-line-item-expanded-${formData.id}`;
    if (itemId) {
      localStorage.setItem(key, itemId);
    } else {
      localStorage.removeItem(key);
    }
  };

  const toggleInvoiceLineItem = (itemId) => {
    const nextId = expandedInvoiceItemId === itemId ? null : itemId;
    setExpandedInvoiceItemId(nextId);
    persistExpandedInvoiceItem(nextId);
  };

  useEffect(() => {
    setShowInvoiceSignatureWarning(Boolean(invoice?.client_signature || invoice?.customer_signature));
  }, [invoice?.id, invoice?.client_signature, invoice?.customer_signature]);

  useEffect(() => {
    calculateTotals();
  }, [formData.line_items, formData.markup_percentage, formData.markup_type, formData.discount_percentage, formData.discount_type, formData.tax_rate, formData.payments, formData.payment_schedule]);

  // Keep ref in sync so auto-save always reads the latest formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!invoiceDidInitAutoSaveRef.current) {
      invoiceDidInitAutoSaveRef.current = true;
      return;
    }
    if (invoiceSkipAutoSaveRef.current) {
      invoiceSkipAutoSaveRef.current = false;
      return;
    }
    if (Date.now() < invoiceAutoSaveBlockUntilRef.current) {
      return;
    }
    if (invoiceAutoSaveTimerRef.current) {
      clearTimeout(invoiceAutoSaveTimerRef.current);
    }
    invoiceAutoSaveTimerRef.current = setTimeout(() => {
      autoSaveInvoice();
    }, 3000);
    return () => {
      if (invoiceAutoSaveTimerRef.current) {
        clearTimeout(invoiceAutoSaveTimerRef.current);
      }
    };
  }, [formData]);

  const calculateTotals = () => {
    setFormData(prev => {
      const subtotal = prev.line_items.reduce((sum, item) => {
        const itemTotal = (item.quantity || 0) * (item.unit_cost || 0);
        const markupAmount = itemTotal * ((item.markup_percentage || 0) / 100);
        return sum + itemTotal + markupAmount;
      }, 0);

      const markupType = prev.markup_type || 'percent';
      const markupAmount = markupType === 'flat'
        ? (prev.markup_percentage || 0)
        : subtotal * ((prev.markup_percentage || 0) / 100);
      const afterMarkup = subtotal + markupAmount;
      const discountType = prev.discount_type || 'percent';
      const discountAmount = discountType === 'flat'
        ? (prev.discount_percentage || 0)
        : afterMarkup * ((prev.discount_percentage || 0) / 100);
      const afterDiscount = afterMarkup - discountAmount;
      const taxAmount = afterDiscount * (prev.tax_rate / 100);
      const total = afterDiscount + taxAmount;

      const schedule = prev.payment_schedule || [];
      const schedType = prev.payment_schedule_type || 'percent';
      let amountPaid, balanceDue;

      // amount_paid = sum of base payment amounts only (CC fees are extra, not credited to invoice balance)
      const allPayments = (prev.payments || []).filter(p => !String(p.method || '').startsWith('Schedule:'));
      const allPaymentsSum = allPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const totalCcFeesPaid = allPayments.reduce((sum, p) => sum + (p.cc_fee_enabled ? (parseFloat(p.cc_fee_amount) || 0) : 0), 0);
      amountPaid = Math.min(allPaymentsSum, total);
      balanceDue = Math.max(0, total - allPaymentsSum);

      // Only update if values actually changed to avoid re-render loops
      const roundedBalance = Math.round(balanceDue * 100) / 100;
      const roundedCcFees = Math.round(totalCcFeesPaid * 100) / 100;
      if (
        prev.subtotal === subtotal &&
        prev.markup_amount === markupAmount &&
        prev.discount_amount === discountAmount &&
        prev.tax_amount === taxAmount &&
        prev.total === total &&
        prev.amount_paid === amountPaid &&
        prev.balance_due === roundedBalance &&
        prev.total_cc_fees_paid === roundedCcFees
      ) {
        return prev;
      }

      return {
        ...prev,
        subtotal,
        markup_amount: markupAmount,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        total,
        amount_paid: amountPaid,
        balance_due: roundedBalance,
        total_cc_fees_paid: roundedCcFees
      };
    });
  };

  const getProvinceTaxSummary = (province) => {
    const components = salesSettings?.province_tax_rates?.[province] || [];
    if (components.length > 0) {
      const totalRate = components.reduce((sum, component) => sum + (parseFloat(component.rate) || 0), 0);
      return { components, totalRate };
    }
    const fallbackRate = salesSettings?.tax_rates?.[province] || 0;
    return {
      components: fallbackRate ? [{ name: 'Tax', rate: fallbackRate }] : [],
      totalRate: fallbackRate
    };
  };

  useEffect(() => {
    if (!salesSettings) return;
    if (formData.tax_rate_manual_override) return;
    const { totalRate } = getProvinceTaxSummary(formData.client_province);
    setFormData(prev => ({ ...prev, tax_rate: totalRate }));
  }, [salesSettings, formData.client_province, formData.tax_rate_manual_override]);

  const addLineItem = (isChangeOrder = false) => {
    const newId = Date.now().toString();
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: newId,
        description: '',
        quantity: 1,
        unit_cost: 0,
        markup_percentage: 0,
        total: 0,
        is_change_order: isChangeOrder,
        change_order_reason: '',
        change_order_id: isChangeOrder ? newId : '',
        change_order_token: '',
        change_order_status: isChangeOrder ? 'PENDING' : '',
        change_order_requires_signature: isChangeOrder ? true : undefined,
        change_order_signature: '',
        change_order_signature_name: '',
        change_order_signed_at: null,
        change_order_approved_method: '',
        change_order_approved_at: null,
        change_order_approved_by: ''
      }]
    }));
    setExpandedInvoiceItemId(newId);
    persistExpandedInvoiceItem(newId);
  };

  const addItemFromLibrary = (item, isChangeOrder = false) => {
    const itemDescription = item.description ? `${item.name}\n${item.description}` : item.name;
    addLineItem(isChangeOrder);
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((li, index) => {
        if (index !== prev.line_items.length - 1) return li;
        return {
          ...li,
          description: itemDescription,
          unit_cost: item.base_cost,
          markup_percentage: item.default_markup_percentage || 0
        };
      })
    }));
    setShowItemSelector(false);
    setItemSearchTerm('');
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeLineItem = async (index) => {
    const targetItem = formData.line_items[index];
    const updatedLineItems = formData.line_items.filter((_, i) => i !== index);
    if (targetItem?.is_change_order) {
      const confirmDelete = window.confirm('Confirm: Delete & Remove Signatures?\nThis will permanently remove this change order from the invoice.');
      if (!confirmDelete) {
        return;
      }
      const invoiceId = getInvoiceId();
      const changeOrderId = targetItem.change_order_id || targetItem.id || targetItem.change_order_token;
      const removeIds = [changeOrderId, targetItem.change_order_token].filter(Boolean);
      if (invoiceId && removeIds.length) {
        if (invoiceAutoSaveTimerRef.current) {
          clearTimeout(invoiceAutoSaveTimerRef.current);
        }
        invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;
        setFormData(prev => ({
          ...prev,
          line_items: updatedLineItems
        }));
        if (targetItem.id && targetItem.id === expandedInvoiceItemId) {
          setExpandedInvoiceItemId(null);
          persistExpandedInvoiceItem(null);
        }
        try {
          const response = await axios.delete(`${API}/sales/invoices/${invoiceId}/change-orders/${removeIds[0]}`);
          let updatedInvoice = applyInvoiceUpdateFromResponse(response);
          if (!updatedInvoice) {
            const fallback = await axios.put(`${API}/sales/invoices/${invoiceId}`, {
              line_items: updatedLineItems,
              remove_change_order_ids: removeIds
            });
            updatedInvoice = applyInvoiceUpdateFromResponse(fallback);
          }
          if (!updatedInvoice) {
            await refreshInvoiceData(invoiceId);
          }
        } catch (error) {
          console.error('Error removing change order:', error);
          alert('Unable to remove this change order.');
          await refreshInvoiceData(invoiceId);
        }
        return;
      }
    }
    const removedId = formData.line_items[index]?.id;
    setFormData(prev => ({
      ...prev,
      line_items: updatedLineItems
    }));
    if (removedId && removedId === expandedInvoiceItemId) {
      setExpandedInvoiceItemId(null);
      persistExpandedInvoiceItem(null);
    }
  };

  const handleSelectExistingCustomer = (value) => {
    setSelectedCustomerKey(value);
    const selected = customerOptions.find((option) => option.key === value);
    if (!selected) {
      return;
    }
    const sourceData = selected.source === 'CRM'
      ? (selected.data.intake_snapshot || selected.data)
      : selected.data;
    setFormData(prev => ({
      ...prev,
      client_name: sourceData.name || selected.data.name || '',
      client_email: sourceData.email || selected.data.email || '',
      client_phone: sourceData.phone || selected.data.phone || '',
      client_address: sourceData.address || selected.data.address || '',
      client_city: sourceData.city || selected.data.city || '',
      client_province: sourceData.province || selected.data.province || '',
      client_postal_code: sourceData.postal_code || selected.data.postal_code || '',
      client_id: selected.source === 'Client' ? selected.data.id : (prev.client_id || ''),
      crm_lead_id: selected.source === 'CRM'
        ? selected.data.id
        : (selected.data.crm_lead_id || prev.crm_lead_id || '')
    }));
  };

  const handleSave = async (status = formData.status || 'DRAFT', options = {}) => {
    const { silent = false, refresh = true } = options;
    // Use ref for silent saves to always get latest data
    const currentData = silent ? formDataRef.current : formData;
    if (!currentData.client_name) {
      if (!silent) {
        alert('Please enter a client name');
      }
      return;
    }
    setIsSaving(true);
    if (silent && Date.now() < invoiceAutoSaveBlockUntilRef.current) {
      setIsSaving(false);
      return false;
    }
    try {
      const baseData = { ...currentData, status };
      const dataToSave = silent
        ? (() => {
            const { payments, amount_paid, balance_due, ...rest } = baseData;
            return rest;
          })()
        : baseData;
      let response;
      if (getInvoiceId()) {
        response = await axios.put(`${API}/sales/invoices/${getInvoiceId()}`, dataToSave);
        if (response?.data && refresh) {
          applyInvoiceUpdate(response.data);
        } else if (status !== formData.status) {
          setFormData(prev => ({ ...prev, status }));
        }
        if (!silent) {
          alert('Invoice updated successfully!');
        }
      } else {
        response = await axios.post(`${API}/sales/invoices`, dataToSave);
        if (response?.data) {
          if (silent) {
            // Auto-save: just update the ID/status without replacing user's in-progress edits
            setFormData(prev => ({ ...prev, id: response.data.id, status: response.data.status || status }));
            invoiceSkipAutoSaveRef.current = true;
            navigate(`/sales/invoices/${response.data.id}`, { replace: true });
          } else {
            applyInvoiceUpdate(response.data);
            onSave?.(response.data);
            navigate(`/sales/invoices/${response.data.id}`);
          }
        }
        if (!silent) {
          alert('Invoice created successfully!');
        }
      }
      if (refresh && getInvoiceId()) {
        onSave?.(response?.data || dataToSave);
      }
      return true;
    } catch (error) {
      console.error('Error saving invoice:', error);
      if (!silent) {
        alert('Error saving invoice. Please try again.');
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const refreshInvoiceData = useCallback(async (invoiceId) => {
    if (!invoiceId) return null;
    try {
      const refreshed = await axios.get(`${API}/sales/invoices/${invoiceId}?t=${Date.now()}`);
      applyInvoiceUpdate(refreshed.data);
      return refreshed.data;
    } catch (error) {
      console.error('Error refreshing invoice data:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) return;
    if (invoiceInitialLoadRef.current === invoiceId) return;
    invoiceInitialLoadRef.current = invoiceId;
    refreshInvoiceData(invoiceId);
  }, [formData.id, invoice?.id, refreshInvoiceData]);

  const hasPendingChangeOrders = (formData.line_items || []).some(item => item.is_change_order && item.change_order_status !== 'APPROVED');

  // NOTE: Polling removed — it was replacing all form state (including unsaved typing) with server data every 5s.
  // Change order approval status is now refreshed on explicit save actions only.

  const autoSaveInvoice = async () => {
    if (isSaving) return;
    const current = formDataRef.current;
    const hasContent = (current.line_items || []).length > 0 || current.client_name || current.client_email;
    if (!hasContent) return;
    setInvoiceAutoSaveState('saving');
    const success = await handleSave(current.status || 'DRAFT', { silent: true, refresh: false });
    if (success) {
      setInvoiceAutoSaveState('idle');
    } else {
      setInvoiceAutoSaveState('error');
    }
  };

  const openSendModal = () => {
    const requestedAmount = formData.requested_amount;
    const hasPaid = Number(formData.amount_paid || 0) > 0;
    setSendForm({
      recipient_email: formData.client_email || '',
      message: salesSettings?.invoice_email_message || '',
      request_type: requestedAmount !== null && requestedAmount !== undefined ? 'amount' : 'full',
      request_value: requestedAmount !== null && requestedAmount !== undefined ? Number(requestedAmount).toFixed(2) : '',
      attach_pdf: false,
      cc_admin: true,
      email_type: hasPaid ? 'receipt' : 'invoice'
    });
    setShowSendModal(true);
  };

  const getChangeOrderLink = (item) => {
    if (!item?.change_order_token) return '';
    return `${window.location.origin}/change-order/${item.change_order_token}`;
  };

  const getChangeOrderStatus = (item) => {
    if (!item?.is_change_order) {
      return { label: 'N/A', tone: 'text-gray-500' };
    }
    if (item.change_order_status === 'APPROVED') {
      if (item.change_order_signature) {
        const label = item.change_order_approved_method === 'manual' ? 'Approved (Manual + Signed)' : 'Approved (Signed)';
        return { label, tone: 'text-emerald-600' };
      }
      if (item.change_order_approved_method === 'manual') {
        return { label: 'Approved (Manual)', tone: 'text-emerald-600' };
      }
      return { label: 'Approved', tone: 'text-emerald-600' };
    }
    return { label: 'Pending', tone: 'text-amber-600' };
  };

  const openChangeOrderSendModal = async (item) => {
    const invoiceId = getInvoiceId();
    if (invoiceId) {
      await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
      const refreshed = await refreshInvoiceData(invoiceId);
      const updatedItem = refreshed?.line_items?.find(li => li.change_order_id === item.change_order_id || li.id === item.id) || item;
      setChangeOrderToSend(updatedItem);
    } else {
      setChangeOrderToSend(item);
    }
    setChangeOrderSendForm({ recipient_email: formData.client_email || '', message: 'Here is the change order for your approval:' });
    setShowChangeOrderSendModal(true);
  };

  const handleSendChangeOrder = async () => {
    if (!changeOrderToSend) return;
    const invoiceId = getInvoiceId();
    if (!invoiceId) {
      alert('Save the invoice before sending a change order.');
      return;
    }
    if (!changeOrderToSend?.change_order_id) {
      alert('Save the change order before sending.');
      return;
    }
    setChangeOrderSending(true);
    try {
      await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
      const response = await axios.post(`${API}/sales/invoices/${invoiceId}/change-orders/${changeOrderToSend.change_order_id}/send`, {
        ...changeOrderSendForm,
        base_url: window.location.origin
      });
      const updatedInvoice = applyInvoiceUpdateFromResponse(response);
      if (!updatedInvoice) {
        await refreshInvoiceData(invoiceId);
      }
      setShowChangeOrderSendModal(false);
      alert('Change order sent for approval.');
    } catch (error) {
      console.error('Error sending change order:', error);
      alert('Unable to send change order.');
    } finally {
      setChangeOrderSending(false);
    }
  };

  const handleClearChangeOrderApproval = async (item) => {
    if (!isAdmin) return;
    if (!window.confirm('Clear this change order approval?')) return;
    const invoiceId = getInvoiceId();
    if (!invoiceId) return;
    try {
      const response = await axios.post(`${API}/sales/invoices/${invoiceId}/change-orders/${item.change_order_id}/clear-approval`);
      const updatedInvoice = applyInvoiceUpdateFromResponse(response);
      if (!updatedInvoice) {
        await refreshInvoiceData(invoiceId);
      }
    } catch (error) {
      console.error('Error clearing change order approval:', error);
      alert('Unable to clear change order approval.');
    }
  };

  const handlePreviewInvoice = async () => {
    try {
      const saved = await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
      if (saved === false) {
        return;
      }
      let token = formData.public_token;
      let number = formData.invoice_number || invoice?.invoice_number || '';
      const invoiceId = formData.id || invoice?.id;
      if (!token && invoiceId) {
        const response = await axios.get(`${API}/sales/invoices/${invoiceId}?t=${Date.now()}`);
        token = response.data?.public_token;
        number = response.data?.invoice_number || number;
        applyInvoiceUpdate(response.data);
      }
      if (!token) {
        alert('Preview link unavailable.');
        return;
      }
      setPreviewInvoiceToken(token);
      setPreviewInvoiceNumber(number);
      setPreviewInvoiceNonce(Date.now());
    } catch (error) {
      console.error('Error opening invoice preview:', error);
      alert('Unable to open preview.');
    }
  };

  const handleOpenInvoiceDocument = async (type) => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) return;
    
    // On mobile, window.open must be called synchronously from user gesture
    // Open the window first, then update its location after save completes
    const isMobile = window.innerWidth < 768;
    
    // Save first
    const saved = await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
    if (saved === false) return;
    
    // Use authenticated PDF fetch for both mobile and desktop
    openAuthenticatedPdf(`${API}/sales/invoices/${invoiceId}/${type}?t=${Date.now()}`);
  };

  const handleCloseInvoicePreview = () => {
    setPreviewInvoiceToken('');
    setPreviewInvoiceNumber('');
    setPreviewInvoiceNonce(0);
  };

  const handleSendInvoice = async () => {
    if (!sendForm.recipient_email) {
      alert('Recipient email is required.');
      return;
    }
    setIsSending(true);
    try {
      let invoiceId = formData.id || invoice?.id;
      if (!invoiceId) {
        const response = await axios.post(`${API}/sales/invoices`, { ...formData, status: 'ISSUED' });
        invoiceId = response.data.id;
      } else {
        await axios.put(`${API}/sales/invoices/${invoiceId}`, { ...formData, status: 'ISSUED' });
      }
      await axios.post(`${API}/sales/invoices/${invoiceId}/send`, {
        ...sendForm,
        base_url: window.location.origin
      });
      alert('Invoice sent successfully.');
      setShowSendModal(false);
      onSave();
    } catch (error) {
      console.error('Error sending invoice:', error);
      const detail = error?.response?.data?.detail;
      alert(detail || 'Unable to send invoice. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleClearInvoiceSignaturesAndEdit = () => {
    setFormData(prev => ({
      ...prev,
      client_signature: '',
      client_signature_name: '',
      client_signature_ip: '',
      status: prev.status === 'PAID' ? 'ISSUED' : prev.status
    }));
    setShowInvoiceSignatureWarning(false);
  };

  const handleDuplicateInvoice = async () => {
    try {
      const payload = {
        ...formData,
        id: undefined,
        invoice_number: 0,
        status: 'DRAFT',
        public_token: undefined,
        email_sent_count: 0,
        email_opened_count: 0,
        email_opened_log: [],
        sent_at: null,
        last_opened_at: null,
        payments: [],
        amount_paid: 0,
        balance_due: formData.total || 0,
        requested_amount: null,
        created_date: undefined,
        last_modified: undefined
      };
      const response = await axios.post(`${API}/sales/invoices`, payload);
      const duplicate = response.data;
      setFormData(duplicate);
      if (onDuplicate) {
        onDuplicate(duplicate);
      }
      setShowInvoiceSignatureWarning(false);
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      alert('Unable to duplicate this invoice.');
    }
  };

  const handleDeleteInvoice = async () => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) return;
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/sales/invoices/${invoiceId}`);
      alert('Invoice deleted.');
      onSave();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Unable to delete invoice.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount) {
      alert('Payment amount is required.');
      return;
    }
    const ccFeeAmt = paymentForm.cc_fee_enabled && paymentForm.cc_fee_value
      ? ((paymentForm.cc_fee_type || 'percent') !== 'flat'
          ? Math.round(parseFloat(paymentForm.amount || 0) * (paymentForm.cc_fee_value || 0) / 100 * 100) / 100
          : parseFloat(paymentForm.cc_fee_value || 0))
      : 0;
    const paymentPayload = {
      amount: parseFloat(paymentForm.amount),
      method: paymentForm.method,
      paid_date: paymentForm.paid_date,
      notes: paymentForm.notes,
      ...(paymentForm.schedule_entry_id ? { schedule_entry_id: paymentForm.schedule_entry_id } : {}),
      cc_fee_enabled: paymentForm.cc_fee_enabled || false,
      cc_fee_type: paymentForm.cc_fee_type || 'percent',
      cc_fee_value: paymentForm.cc_fee_value || 0,
      cc_fee_amount: ccFeeAmt
    };

    try {
      const invoiceId = getInvoiceId();
      invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;
      invoiceSkipAutoSaveRef.current = true;
      if (invoiceAutoSaveTimerRef.current) {
        clearTimeout(invoiceAutoSaveTimerRef.current);
      }
      if (invoiceId) {
        // Force-save first to ensure DB has latest schedule/data before recording payment
        await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
        invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;

        const response = await axios.post(`${API}/sales/invoices/${invoiceId}/payments`, paymentPayload);
        const updatedInvoice = applyInvoiceUpdateFromResponse(response);
        if (!updatedInvoice) {
          const optimisticPayment = {
            id: `temp-${Date.now()}`,
            ...paymentPayload
          };
          setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, optimisticPayment]
          }));
          await new Promise(resolve => setTimeout(resolve, 300));
          await refreshInvoiceData(invoiceId);
        }
      } else {
        setFormData(prev => ({
          ...prev,
          payments: [...prev.payments, { id: Date.now().toString(), ...paymentPayload }]
        }));
      }
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', method: 'Manual', paid_date: new Date().toISOString().split('T')[0], notes: '', schedule_entry_id: '', payFullAmount: false, cc_fee_enabled: false, cc_fee_type: 'percent', cc_fee_value: 0 });
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Unable to record payment.');
    }
  };

  const handleDeletePayment = async (paymentId, index) => {
    if (!isAdmin) return;
    if (!window.confirm('Delete this payment? This cannot be undone.')) return;
    const invoiceId = getInvoiceId();
    invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;
    try {
      invoiceSkipAutoSaveRef.current = true;
      if (invoiceAutoSaveTimerRef.current) {
        clearTimeout(invoiceAutoSaveTimerRef.current);
      }
      if (invoiceId) {
        // Force-save first to ensure DB has latest data
        await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
        invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;

        const encodedId = paymentId ? encodeURIComponent(paymentId) : 'missing';
        const response = await axios.delete(`${API}/sales/invoices/${invoiceId}/payments/${encodedId}`, {
          params: paymentId ? undefined : { index }
        });
        const updatedInvoice = applyInvoiceUpdateFromResponse(response);
        if (!updatedInvoice) {
          await refreshInvoiceData(invoiceId);
        }
      } else {
        setFormData(prev => ({
          ...prev,
          payments: prev.payments.filter((_, idx) => idx !== index)
        }));
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Unable to delete payment.');
    }
  };

  const openEditPaymentModal = (payment) => {
    setEditPaymentId(payment.id);
    setEditPaymentForm({
      amount: payment.amount || '',
      method: payment.method || 'Manual',
      paid_date: payment.paid_date ? new Date(payment.paid_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      notes: payment.notes || '',
      schedule_entry_id: payment.schedule_entry_id || '',
      cc_fee_enabled: payment.cc_fee_enabled || false,
      cc_fee_type: payment.cc_fee_type || 'percent',
      cc_fee_value: payment.cc_fee_value || 0
    });
    setShowEditPaymentModal(true);
  };

  const handleEditPayment = async () => {
    if (!editPaymentForm.amount) {
      alert('Payment amount is required.');
      return;
    }
    const invoiceId = getInvoiceId();
    if (!invoiceId || !editPaymentId) return;
    try {
      invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;
      invoiceSkipAutoSaveRef.current = true;
      if (invoiceAutoSaveTimerRef.current) {
        clearTimeout(invoiceAutoSaveTimerRef.current);
      }
      // Force-save first to ensure DB has latest data
      await handleSave(formData.status || 'DRAFT', { silent: true, refresh: false });
      invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;

      const ccFeeAmtEdit = editPaymentForm.cc_fee_enabled && editPaymentForm.cc_fee_value
        ? ((editPaymentForm.cc_fee_type || 'percent') !== 'flat'
            ? Math.round(parseFloat(editPaymentForm.amount || 0) * (editPaymentForm.cc_fee_value || 0) / 100 * 100) / 100
            : parseFloat(editPaymentForm.cc_fee_value || 0))
        : 0;
      const response = await axios.put(`${API}/sales/invoices/${invoiceId}/payments/${encodeURIComponent(editPaymentId)}`, {
        amount: parseFloat(editPaymentForm.amount),
        method: editPaymentForm.method,
        paid_date: editPaymentForm.paid_date,
        notes: editPaymentForm.notes,
        schedule_entry_id: editPaymentForm.schedule_entry_id || null,
        cc_fee_enabled: editPaymentForm.cc_fee_enabled || false,
        cc_fee_type: editPaymentForm.cc_fee_type || 'percent',
        cc_fee_value: editPaymentForm.cc_fee_value || 0,
        cc_fee_amount: ccFeeAmtEdit
      });
      applyInvoiceUpdateFromResponse(response);
      setShowEditPaymentModal(false);
      setEditPaymentId(null);
    } catch (error) {
      console.error('Error editing payment:', error);
      alert('Unable to update payment.');
    }
  };

  const updateScheduleItem = (index, field, value) => {
    // Block autosave while editing schedule to prevent race conditions
    invoiceAutoSaveBlockUntilRef.current = Date.now() + 3000;
    setFormData(prev => {
      const schedule = prev.payment_schedule || [];
      const updated = schedule.map((item, idx) => {
        if (idx !== index) return item;
        if (field === 'amount') {
          const numeric = value === '' ? 0 : parseFloat(value);
          return { ...item, amount: Number.isNaN(numeric) ? 0 : numeric };
        }
        return { ...item, [field]: value };
      });
      return { ...prev, payment_schedule: updated };
    });
  };

  const getScheduleItemAmount = (item) => {
    let amountValue = parseFloat(item?.amount || 0);
    if (Number.isNaN(amountValue)) {
      amountValue = 0;
    }
    if (scheduleType === 'percent') {
      const totalValue = Number(formData.total || 0);
      amountValue = (totalValue * amountValue) / 100;
    }
    return Math.round(amountValue * 100) / 100;
  };

  const handleSchedulePaidToggle = async (index, checked) => {
    const schedule = formData.payment_schedule || [];
    const item = schedule[index];
    if (!item || !item.id) return;

    const invoiceId = getInvoiceId();
    if (!invoiceId) return;

    // Immediately update UI so the toggle visually responds
    setFormData(prev => ({
      ...prev,
      payment_schedule: (prev.payment_schedule || []).map((entry, idx) =>
        idx === index ? { ...entry, paid: checked } : entry
      )
    }));

    // Block autosave during the operation
    invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;
    invoiceSkipAutoSaveRef.current = true;
    if (invoiceAutoSaveTimerRef.current) {
      clearTimeout(invoiceAutoSaveTimerRef.current);
    }

    try {
      const response = await axios.post(`${API}/sales/invoices/${invoiceId}/schedule-toggle`, {
        entry_id: item.id,
        paid: checked
      });
      applyInvoiceUpdateFromResponse(response);
    } catch (error) {
      console.error('Toggle failed:', error);
      // Revert on failure
      try {
        const freshInvoice = await axios.get(`${API}/sales/invoices/${invoiceId}`);
        applyInvoiceUpdateFromResponse(freshInvoice);
      } catch (_) { /* ignore */ }
    }
  };

  const addScheduleItem = () => {
    // Block autosave while modifying schedule to prevent race conditions
    invoiceAutoSaveBlockUntilRef.current = Date.now() + 3000;
    setFormData(prev => ({
      ...prev,
      payment_schedule_type: prev.payment_schedule_type || 'percent',
      payment_schedule: [
        ...(prev.payment_schedule || []),
        { id: `schedule-${Date.now()}`, name: '', amount: 0, paid: false }
      ]
    }));
  };

  const deleteScheduleItem = (index) => {
    // Block autosave while modifying schedule to prevent race conditions
    invoiceAutoSaveBlockUntilRef.current = Date.now() + 3000;
    setFormData(prev => ({
      ...prev,
      payment_schedule: (prev.payment_schedule || []).filter((_, idx) => idx !== index)
    }));
  };

  const getSchedulePaymentMatch = (item) => {
    if (!item) return null;
    const payments = formData.payments || [];
    if (item.payment_id) {
      const direct = payments.find(payment => payment.id === item.payment_id);
      if (direct) return direct;
    }
    const scheduleKey = item.id ? `schedule:${item.id}` : '';
    const byNote = scheduleKey ? payments.find(payment => (payment.notes || '').includes(scheduleKey)) : null;
    if (byNote) return byNote;
    const methodLabel = `Schedule: ${item.name || ''}`.trim();
    return payments.find(payment => payment.method === methodLabel) || null;
  };

  const getScheduleDisplayAmount = (item) => {
    const percentValue = Number(item?.amount || 0);
    const computed = getScheduleItemAmount(item);
    if (scheduleType === 'percent') {
      return `${percentValue.toFixed(2)}% ($${formatCurrency(computed)})`;
    }
    return `$${formatCurrency(computed)}`;
  };

  const getScheduleEntryRemaining = (item) => {
    const entryTotal = getScheduleItemAmount(item);
    const paidAmount = parseFloat(item?.paid_amount || 0);
    return Math.max(0, entryTotal - paidAmount);
  };

  const getScheduleEntryOptions = () => {
    const items = formData.payment_schedule || [];
    return items.filter(item => !item.paid).map(item => {
      const entryTotal = getScheduleItemAmount(item);
      const paidSoFar = parseFloat(item?.paid_amount || 0);
      const remaining = Math.max(0, entryTotal - paidSoFar);
      return {
        id: item.id,
        name: item.name || 'Unnamed',
        entryTotal,
        paidSoFar,
        remaining
      };
    });
  };

  const latestPayment = formData.payments && formData.payments.length
    ? [...formData.payments].sort((a, b) => new Date(b.paid_date) - new Date(a.paid_date))[0]
    : null;
  const scheduleItems = formData.payment_schedule || [];
  const scheduleType = formData.payment_schedule_type || 'percent';
  const scheduleAllocatedTotal = scheduleItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const scheduleRemaining = scheduleType === 'percent'
    ? 100 - scheduleAllocatedTotal
    : (Number(formData.total || 0) - scheduleAllocatedTotal);
  const scheduleRemainingLabel = scheduleType === 'percent'
    ? scheduleRemaining === 0
      ? '100% allocated'
      : scheduleRemaining < 0
        ? `${Math.abs(scheduleRemaining).toFixed(0)}% over-allocated`
        : `${scheduleRemaining.toFixed(0)}% remaining to allocate`
    : scheduleRemaining === 0
      ? 'Fully allocated'
      : scheduleRemaining < 0
        ? `$${formatCurrency(Math.abs(scheduleRemaining))} over-allocated`
        : `$${formatCurrency(scheduleRemaining)} remaining to allocate`;
  const changeOrderItems = (formData.line_items || []).filter(item => item.is_change_order);
  const changeOrderRows = changeOrderItems.map((item, index) => {
    const label = (item.change_order_reason || item.description || '').split('\n')[0].trim() || `Change Order ${index + 1}`;
    const amount = (Number(item.quantity || 0) * Number(item.unit_cost || 0))
      * (1 + (Number(item.markup_percentage || 0) / 100));
    return { label, amount };
  });
  const nextScheduleItem = scheduleItems.find(item => !item.paid);
  const formatScheduleDisplayAmount = (item) => getScheduleDisplayAmount(item);
  // Compute the next payment label for display
  const computeNextPaymentLabel = () => {
    if (!nextScheduleItem) return '';
    const computed = getScheduleItemAmount(nextScheduleItem);
    const paidSoFar = parseFloat(nextScheduleItem.paid_amount || 0);
    const remaining = Math.max(0, computed - paidSoFar);
    const balVal = Number(formData.balance_due || 0);
    const capped = Math.min(remaining, balVal);
    return `$${formatCurrency(capped)}`;
  };
  const nextPaymentLabel = computeNextPaymentLabel();
  const requestValueNumber = parseFloat(sendForm.request_value);
  const requestedAmount = sendForm.request_type === 'amount'
    ? (Number.isNaN(requestValueNumber) ? 0 : requestValueNumber)
    : sendForm.request_type === 'percent'
      ? (Number.isNaN(requestValueNumber) ? 0 : (formData.total * requestValueNumber) / 100)
      : formData.balance_due;
  const activeInvoiceId = getInvoiceId();

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="sales-editor-mobile-scale" data-testid="invoice-editor-scale-wrapper">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Mobile header */}
          <div className="sm:hidden">
            <div className="grid grid-cols-3 items-center gap-2">
              <button
                onClick={onBack}
                className="text-emerald-600 font-semibold text-sm flex items-center gap-1"
                data-testid="invoice-editor-back-mobile"
              >
                ← Back
              </button>
              <div className="text-center text-base font-semibold text-gray-900 truncate" data-testid="invoice-editor-title-mobile">
                {invoice?.id ? `#${invoice.invoice_number}` : 'New'}
              </div>
              <div className="flex justify-end relative">
                <button
                  onClick={() => setShowInvoiceActionsMenu(prev => !prev)}
                  className="text-emerald-600 font-semibold text-sm flex items-center gap-1"
                  data-testid="invoice-actions-toggle-mobile"
                >
                  Actions ▾
                </button>
                {showInvoiceActionsMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-testid="invoice-actions-menu-mobile">
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setShowInvoiceActionsMenu(false);
                          openSendModal();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Send Invoice
                      </button>
                      <button
                        onClick={() => {
                          setShowInvoiceActionsMenu(false);
                          handlePreviewInvoice();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => {
                          setShowInvoiceActionsMenu(false);
                          handleOpenInvoicePdf();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        PDF / Receipt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div
              className={`text-xs mt-1 ${invoiceAutoSaveState === 'error' ? 'text-red-600' : 'text-gray-500'}`}
              data-testid="invoice-autosave-status-mobile"
            >
              {invoiceAutoSaveState === 'error' ? 'Save failed' : 'Auto-save on'}
            </div>
          </div>
          
          {/* Desktop header */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
                data-testid="invoice-editor-back"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-800" data-testid="invoice-editor-title">
                {invoice?.id ? `Edit Invoice #${invoice.invoice_number}` : 'New Invoice'}
              </h1>
            </div>
            <div className="flex flex-wrap gap-3 justify-end items-center">
              <div
                className={`text-xs ${invoiceAutoSaveState === 'error' ? 'text-red-600' : 'text-gray-500'}`}
                data-testid="invoice-autosave-status"
              >
                {invoiceAutoSaveState === 'error' ? 'Save failed' : 'Auto-save on'}
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowInvoiceActionsMenu(prev => !prev)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2"
                  data-testid="invoice-actions-toggle"
                >
                  Actions ▾
                </button>
                {showInvoiceActionsMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-testid="invoice-actions-menu">
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setShowInvoiceActionsMenu(false);
                          openSendModal();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="invoice-actions-send"
                      >
                        Send Invoice
                      </button>
                      <button
                        onClick={() => {
                          setShowInvoiceActionsMenu(false);
                          handlePreviewInvoice();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="invoice-actions-preview"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => {
                          setShowInvoiceActionsMenu(false);
                          handleOpenInvoiceDocument('pdf');
                        }}
                        disabled={!activeInvoiceId}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                        data-testid="invoice-actions-pdf"
                      >
                        PDF / Receipt
                      </button>
                      {activeInvoiceId && (
                        <button
                          onClick={() => {
                            setShowInvoiceActionsMenu(false);
                            handleDeleteInvoice();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          data-testid="invoice-actions-delete"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="hidden">
              <button
                onClick={() => handleSave('DRAFT')}
                disabled={isSaving}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold disabled:bg-gray-400"
                data-testid="invoice-save-draft"
              >
                {isSaving ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                onClick={() => handleSave('ISSUED')}
                disabled={isSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-semibold disabled:bg-gray-400"
                data-testid="invoice-save"
              >
                {isSaving ? 'Saving...' : 'Save Invoice'}
              </button>
              <button
                onClick={openSendModal}
                disabled={isSending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold disabled:bg-gray-400"
                data-testid="invoice-send"
              >
                {isSending ? 'Sending...' : 'Send Invoice'}
              </button>
              <button
                onClick={() => handleOpenInvoiceDocument('pdf')}
                disabled={!activeInvoiceId}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold disabled:text-gray-400"
                data-testid="invoice-download-pdf"
              >
                PDF / Receipt
              </button>
              <button
                onClick={() => publicLink && window.open(`${publicLink}?t=${Date.now()}`, '_blank')}
                disabled={!publicLink}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold disabled:text-gray-400"
                data-testid="invoice-public-link"
              >
                Public Link
              </button>
              {activeInvoiceId && (
                <button
                  onClick={handleDeleteInvoice}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold disabled:bg-gray-400"
                  data-testid="invoice-delete"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Client Information</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Existing Customer</label>
            <select
              value={selectedCustomerKey}
              onChange={(e) => handleSelectExistingCustomer(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="invoice-existing-customer-select"
            >
              <option value="">Choose a customer</option>
              {customerOptions.map(option => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.client_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={formData.client_address}
                onChange={(e) => setFormData(prev => ({ ...prev, client_address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={formData.client_city}
                onChange={(e) => setFormData(prev => ({ ...prev, client_city: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
              <select
                value={formData.client_province}
                onChange={(e) => setFormData(prev => ({ ...prev, client_province: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-province"
              >
                <option value="ON">Ontario</option>
                <option value="AB">Alberta</option>
                <option value="BC">British Columbia</option>
                <option value="MB">Manitoba</option>
                <option value="NB">New Brunswick</option>
                <option value="NL">Newfoundland and Labrador</option>
                <option value="NS">Nova Scotia</option>
                <option value="PE">Prince Edward Island</option>
                <option value="QC">Quebec</option>
                <option value="SK">Saskatchewan</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input
                type="text"
                value={formData.client_postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, client_postal_code: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-client-postal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-due-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
              <input
                type="text"
                value={formData.po_number}
                onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="invoice-po-number"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800" data-testid="invoice-line-items-title">Line Items</h3>
            <div className="flex flex-wrap gap-2">
              {changeOrderMode && (
                <>
                  <button
                    onClick={() => setShowItemSelector(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium"
                    data-testid="invoice-change-order-select-library"
                  >
                    📚 Select from Library
                  </button>
                  <button
                    onClick={() => addLineItem(true)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium"
                    data-testid="invoice-change-order-add-line-item"
                  >
                    + Add Line Item
                  </button>
                  <button
                    onClick={() => setChangeOrderMode(false)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
                    data-testid="invoice-change-order-done"
                  >
                    Done
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setChangeOrderMode(true);
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
                data-testid="invoice-change-order-start"
              >
                + Change Order
              </button>
            </div>
          </div>

          {showItemSelector && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto" data-testid="invoice-item-selector-modal">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col my-auto sm:my-0">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Select Item from Library</h3>
                    <button
                      onClick={() => setShowItemSelector(false)}
                      className="text-gray-600 hover:text-gray-800 text-2xl"
                      data-testid="invoice-item-selector-close"
                    >
                      ×
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={itemSearchTerm}
                    onChange={(e) => setItemSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    data-testid="invoice-item-selector-search"
                  />
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                  {libraryItems.length === 0 ? (
                    <div className="text-center py-12 text-gray-600">No items in library yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {libraryItems
                        .filter(item => item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()))
                        .map(libItem => (
                          <button
                            key={libItem.id}
                            onClick={() => addItemFromLibrary(libItem, changeOrderMode)}
                            className="w-full text-left border border-gray-300 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-500"
                            data-testid={`invoice-item-selector-item-${libItem.id}`}
                          >
                            <h4 className="font-semibold text-gray-800">{libItem.name}</h4>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <div className="p-6 border-t">
                  <button
                    onClick={() => setShowItemSelector(false)}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
                    data-testid="invoice-item-selector-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {formData.line_items.map((item, index) => {
              const isExpanded = expandedInvoiceItemId === item.id;
              const itemTitle = (item.description || '').split('\n')[0].trim() || 'Line Item';
              const lineTotalValue = (item.quantity * item.unit_cost) * (1 + item.markup_percentage / 100);
              const lineTotalLabel = formatCurrency(lineTotalValue);
              const changeOrderSignatureSrc = item.change_order_signature
                ? (item.change_order_signature.startsWith('data:image')
                  ? item.change_order_signature
                  : `data:image/png;base64,${item.change_order_signature}`)
                : '';
              const hasChangeOrderSignatureImage = changeOrderSignatureSrc && changeOrderSignatureSrc.length > 50;
              return (
                <div key={item.id} className={`border border-gray-300 rounded-lg p-4 ${item.is_change_order ? 'bg-orange-50 border-orange-200' : ''}`} data-testid={`invoice-line-item-${index}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid={`invoice-line-title-${index}`}>
                        {itemTitle}
                        {item.is_change_order && (
                          <span className="ml-2 text-xs uppercase tracking-wide bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Change Order</span>
                        )}
                        {item.is_change_order && (
                          <span className={`ml-2 text-xs font-semibold ${getChangeOrderStatus(item).tone}`} data-testid={`invoice-change-order-status-inline-${index}`}>
                            {getChangeOrderStatus(item).label}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-2 whitespace-pre-wrap" data-testid={`invoice-line-summary-${index}`}>
                        Qty {item.quantity} • Unit ${formatCurrency(item.unit_cost)} • Total ${lineTotalLabel}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleInvoiceLineItem(item.id)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-semibold"
                        data-testid={`invoice-line-toggle-${index}`}
                      >
                        {isExpanded ? 'Hide' : 'Details'}
                      </button>
                      <button
                        onClick={() => removeLineItem(index)}
                        className="text-red-600 hover:text-red-700 text-sm font-semibold"
                        data-testid={`invoice-line-remove-${index}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="mt-4 space-y-3">
                      {item.is_change_order && (
                        <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-2" data-testid={`invoice-change-order-panel-${index}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase text-orange-700">Change Order Approval</span>
                            <span className={`text-xs font-semibold ${getChangeOrderStatus(item).tone}`} data-testid={`invoice-change-order-status-${index}`}>
                              {getChangeOrderStatus(item).label}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600" data-testid={`invoice-change-order-summary-${index}`}>
                            This change order will be added to Invoice #{formData.invoice_number || formData.id || ''}.
                          </div>
                          {item.change_order_status === 'APPROVED' && (
                            <div className="text-xs text-gray-500" data-testid={`invoice-change-order-approved-${index}`}>
                              Approved {item.change_order_approved_method === 'manual' ? 'manually' : 'by signature'}{item.change_order_approved_by ? ` by ${item.change_order_approved_by}` : ''}{item.change_order_approved_at ? ` on ${new Date(item.change_order_approved_at).toLocaleDateString()}` : ''}.
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => openChangeOrderSendModal(item)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-semibold"
                              data-testid={`invoice-change-order-send-${index}`}
                            >
                              Send for Approval
                            </button>
                            {item.change_order_token && (
                              <button
                                type="button"
                                onClick={() => window.open(getChangeOrderLink(item), '_blank')}
                                className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold text-gray-700"
                                data-testid={`invoice-change-order-view-${index}`}
                              >
                                View Link
                              </button>
                            )}
                            {isAdmin && item.change_order_status === 'APPROVED' && (
                              <button
                                type="button"
                                onClick={() => handleClearChangeOrderApproval(item)}
                                className="px-3 py-1.5 border border-red-200 text-red-600 rounded text-xs font-semibold"
                                data-testid={`invoice-change-order-clear-${index}`}
                              >
                                Clear Approval
                              </button>
                            )}
                          </div>
                          {item.change_order_signature && (
                            <div className="mt-2" data-testid={`invoice-change-order-signature-${index}`}>
                              <div className="text-xs text-gray-500">Signed by {item.change_order_signature_name || formData.client_name || 'Customer'}{item.change_order_signed_at ? ` on ${new Date(item.change_order_signed_at).toLocaleDateString()}` : ''}</div>
                              {hasChangeOrderSignatureImage ? (
                                <img src={changeOrderSignatureSrc} alt="Change order signature" className="border border-gray-200 rounded mt-2 max-h-28" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}
                      <textarea
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[180px]"
                        rows="6"
                        placeholder="Item title on first line, details on next line"
                        data-testid={`invoice-line-description-${index}`}
                      />
                      {item.is_change_order && (
                        <input
                          type="text"
                          value={item.change_order_reason || ''}
                          onChange={(e) => updateLineItem(index, 'change_order_reason', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg"
                          placeholder="Change order reason"
                          data-testid={`invoice-line-change-reason-${index}`}
                        />
                      )}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            step="0.01"
                            data-testid={`invoice-line-quantity-${index}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Unit Cost ($)</label>
                          <input
                            type="number"
                            value={item.unit_cost}
                            onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            step="0.01"
                            data-testid={`invoice-line-unit-cost-${index}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Markup (%)</label>
                          <input
                            type="number"
                            value={item.markup_percentage}
                            onChange={(e) => updateLineItem(index, 'markup_percentage', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            step="0.1"
                            data-testid={`invoice-line-markup-${index}`}
                          />
                        </div>
                      </div>
                      <div className="text-right font-bold text-gray-800" data-testid={`invoice-line-total-${index}`}>
                        Total: ${lineTotalLabel}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-testid="invoice-totals-card">
          <div className="space-y-3 max-w-md ml-auto text-sm text-gray-700" data-testid="invoice-totals-panel">
            <div className="grid grid-cols-[1fr_auto] items-center">
              <span>Subtotal</span>
              <span className="font-semibold text-gray-900" data-testid="invoice-subtotal">${formatCurrency(formData.subtotal)}</span>
            </div>
            {changeOrderRows.length > 0 && (
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-3 space-y-2" data-testid="invoice-change-order-summary">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide" data-testid="invoice-change-order-title">Change Orders</div>
                {changeOrderRows.map((order, index) => (
                  <div key={`${order.label}-${index}`} className="grid grid-cols-[1fr_auto] items-center text-sm text-gray-700" data-testid={`invoice-change-order-${index}`}>
                    <span className="truncate">{order.label}</span>
                    <span className="font-semibold text-gray-900">${formatCurrency(order.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto] items-center">
              <div className="flex items-center gap-2">
                <span>Markup</span>
                <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, markup_type: 'percent' }))} className={`px-2 py-0.5 transition-colors ${(formData.markup_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-testid="invoice-markup-type-percent">%</button>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, markup_type: 'flat' }))} className={`px-2 py-0.5 transition-colors ${formData.markup_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-testid="invoice-markup-type-flat">$</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" value={formData.markup_percentage} onChange={(e) => setFormData(prev => ({ ...prev, markup_percentage: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" step="0.01" data-testid="invoice-markup-percentage" />
                <span className="font-semibold text-gray-900" data-testid="invoice-markup-amount">${formatCurrency(formData.markup_amount)}</span>
              </div>
            </div>
            <div className="grid grid-cols-[1fr_auto] items-center">
              <div className="flex items-center gap-2">
                <span>Discount</span>
                <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, discount_type: 'percent' }))} className={`px-2 py-0.5 transition-colors ${(formData.discount_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-testid="invoice-discount-type-percent">%</button>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, discount_type: 'flat' }))} className={`px-2 py-0.5 transition-colors ${formData.discount_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-testid="invoice-discount-type-flat">$</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" value={formData.discount_percentage} onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" step="0.01" data-testid="invoice-discount-percentage" />
                <span className="font-semibold text-gray-900" data-testid="invoice-discount-amount">-${formatCurrency(formData.discount_amount)}</span>
              </div>
            </div>
            <div className="grid grid-cols-[1fr_auto] items-center">
              <span>Tax (%)</span>
              <div className="flex items-center gap-2">
                <input type="number" value={formData.tax_rate} onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0, tax_rate_manual_override: true }))} className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" step="0.1" data-testid="invoice-tax-rate" />
                <span className="font-semibold text-gray-900" data-testid="invoice-tax-amount">${formatCurrency(formData.tax_amount)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span data-testid="invoice-tax-breakdown">
                Province taxes: {(getProvinceTaxSummary(formData.client_province).components || [])
                  .map(component => `${component.name} ${component.rate}%`).join(' + ') || 'N/A'}
              </span>
              <button type="button" onClick={() => { const { totalRate } = getProvinceTaxSummary(formData.client_province); setFormData(prev => ({ ...prev, tax_rate: totalRate, tax_rate_manual_override: false })); }} className="text-blue-600 hover:text-blue-700 font-medium" data-testid="invoice-tax-use-province">
                Use province rate
              </button>
            </div>
            {/* CC Processing Fee moved to individual payments — see Record Payment modal */}
            <div className="border-t border-gray-900 pt-3 grid grid-cols-[1fr_auto] items-center text-base">
              <span className="font-bold text-gray-800">Total (CAD)</span>
              <span className="font-bold text-gray-900" data-testid="invoice-total">${formatCurrency(formData.total)}</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] items-center text-sm">
              <span className="font-semibold text-gray-700">Amount Paid</span>
              <span className="font-semibold text-emerald-600" data-testid="invoice-amount-paid-total">${formatCurrency(formData.amount_paid)}</span>
            </div>
            {Number(formData.total_cc_fees_paid) > 0 && (
              <div className="grid grid-cols-[1fr_auto] items-center text-sm">
                <span className="text-blue-600">CC Fees</span>
                <span className="text-blue-600" data-testid="invoice-total-cc-fees">+${formatCurrency(formData.total_cc_fees_paid)}</span>
              </div>
            )}
            {Number(formData.total_cc_fees_paid) > 0 && (
              <div className="grid grid-cols-[1fr_auto] items-center text-sm">
                <span className="font-semibold text-gray-700">Total Paid</span>
                <span className="font-semibold text-emerald-700" data-testid="invoice-total-paid">${formatCurrency(Number(formData.amount_paid || 0) + Number(formData.total_cc_fees_paid || 0))}</span>
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto] items-center text-lg">
              <span className="font-bold text-gray-800">Remaining Amount</span>
              <span className="font-bold text-red-600" data-testid="invoice-balance-due-total">${formatCurrency(formData.balance_due)}</span>
            </div>

            {/* Payment Schedule — Joist-style row */}
            <div className="border-t border-gray-200 pt-3" data-testid="invoice-payment-schedule-summary">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Payment Schedule</span>
                <button
                  type="button"
                  onClick={() => {
                    scheduleSnapshotRef.current = {
                      payment_schedule: JSON.parse(JSON.stringify(formData.payment_schedule || [])),
                      payment_schedule_type: formData.payment_schedule_type || 'percent'
                    };
                    setShowScheduleModal(true);
                  }}
                  className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm"
                  data-testid="invoice-payment-schedule-edit"
                >
                  {scheduleItems.length > 0 ? 'Edit' : 'Add'}
                </button>
              </div>
              {scheduleItems.length > 0 && (() => {
                // FIFO allocation: apply payments sequentially across milestones
                let remaining = Number(formData.amount_paid || 0);
                // Build mutable payment list for CC fee FIFO tracking
                const schedPayments = (formData.payments || [])
                  .filter(p => !String(p.method || '').startsWith('Schedule:'))
                  .map(p => ({
                    left: parseFloat(p.amount || 0),
                    ccFee: p.cc_fee_enabled ? (parseFloat(p.cc_fee_amount || 0)) : 0,
                    total: Math.max(parseFloat(p.amount || 0), 0.01)
                  }));
                let pmtPtr = 0;
                const allocs = scheduleItems.map(item => {
                  const due = scheduleType === 'percent'
                    ? (Number(formData.total || 0)) * (parseFloat(item.amount || 0) / 100)
                    : parseFloat(item.amount || 0);
                  const paid = Math.min(due, remaining);
                  remaining = Math.max(0, remaining - paid);
                  // FIFO CC fee allocation
                  let ccFeeForEntry = 0;
                  let need = paid;
                  let i = pmtPtr;
                  while (need > 0.001 && i < schedPayments.length) {
                    const sp = schedPayments[i];
                    const use = Math.min(need, sp.left);
                    ccFeeForEntry += (use / sp.total) * sp.ccFee;
                    need -= use;
                    sp.left -= use;
                    if (sp.left <= 0.001) i++;
                  }
                  pmtPtr = i;
                  return { ...item, due, paid, balance: due - paid, ccFeeFromPayment: Math.round(ccFeeForEntry * 100) / 100 };
                });
                return (
                  <div className="mt-2 space-y-2" data-testid="invoice-payment-schedule-table">
                    {allocs.map((a, index) => (
                      <div key={a.id || index} data-testid={`invoice-payment-schedule-table-row-${index}`}>
                        <div className="flex justify-between text-xs text-gray-600">
                          <span className="truncate mr-2">{a.name || `Payment ${index + 1}`}</span>
                          <span className="font-medium text-gray-700 flex-shrink-0">{getScheduleDisplayAmount(a)}</span>
                        </div>
                        {a.due > 0 && (
                          <div className="flex flex-col items-end mt-0.5 gap-0.5">
                            {a.balance <= 0 ? (
                              <>
                                <span className="text-xs text-emerald-600 font-semibold" data-testid={`invoice-payment-schedule-progress-${index}`}>✓ Paid in Full</span>
                                {a.ccFeeFromPayment > 0 && (
                                  <span className="text-xs text-blue-600" data-testid={`invoice-payment-schedule-cc-fee-${index}`}>CC fee: +${formatCurrency(a.ccFeeFromPayment)}</span>
                                )}
                              </>
                            ) : a.paid > 0 ? (
                              <span className="text-xs text-amber-600 font-medium" data-testid={`invoice-payment-schedule-progress-${index}`}>
                                Paid ${formatCurrency(a.paid)} · <strong>${formatCurrency(a.balance)} remaining</strong>
                              </span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Payments Section - Joist-style */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-testid="invoice-recorded-payments-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">Payments</h3>
            {isAdmin && (
              <button
                onClick={() => {
                  setPaymentForm(prev => ({
                    ...prev,
                    cc_fee_type: salesSettings?.default_cc_fee_type || 'percent',
                    cc_fee_value: salesSettings?.default_cc_fee_value || 0
                  }));
                  setShowPaymentModal(true);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold text-sm uppercase tracking-wide"
                data-testid="invoice-record-payment-btn"
              >
                Record Payment
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div className="mb-4" data-testid="invoice-payment-progress">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-semibold text-emerald-600" data-testid="invoice-paid-label">PAID ${formatCurrency(formData.amount_paid)}</span>
              <span className="font-semibold text-gray-700" data-testid="invoice-due-label">DUE ${formatCurrency(formData.balance_due)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-emerald-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${formData.total > 0 ? Math.min(100, (formData.amount_paid / formData.total) * 100) : 0}%` }}
                data-testid="invoice-payment-progress-bar"
              />
            </div>
          </div>

          {/* Payments list */}
          {(() => {
            const realPayments = (formData.payments || []).filter(p => !String(p.method || '').startsWith('Schedule:'));
            return realPayments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4" data-testid="invoice-no-payments">No payments recorded yet.</p>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="invoice-payments-list">
              {realPayments.map((payment, index) => (
                <div
                  key={payment.id || index}
                  className="flex items-center justify-between py-3"
                  data-testid={`invoice-payment-row-${index}`}
                >
                  <div>
                    <div className="text-base font-semibold text-gray-900" data-testid={`invoice-payment-amount-${index}`}>
                      ${formatCurrency(payment.amount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5" data-testid={`invoice-payment-meta-${index}`}>
                      {payment.paid_date ? (() => {
                        const d = String(payment.paid_date).split('T')[0];
                        const parts = d.split('-');
                        if (parts.length === 3) {
                          const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
                          return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
                        }
                        return d;
                      })() : ''}{payment.method ? ` · ${payment.method}` : ''}
                    </div>
                    {payment.notes && (
                      <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs" data-testid={`invoice-payment-notes-${index}`}>
                        {payment.notes}
                      </div>
                    )}
                    {payment.cc_fee_enabled && Number(payment.cc_fee_amount) > 0 && (
                      <div className="text-xs text-blue-600 mt-0.5 font-medium" data-testid={`invoice-payment-cc-fee-${index}`}>
                        CC fee: +${formatCurrency(payment.cc_fee_amount)}{payment.cc_fee_type !== 'flat' ? ` (${payment.cc_fee_value}%)` : ''} · Total charged: ${formatCurrency(Number(payment.amount) + Number(payment.cc_fee_amount))}
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-3 ml-3">
                      <button onClick={() => openEditPaymentModal(payment)} className="text-gray-400 hover:text-blue-600 text-sm" data-testid={`invoice-payment-edit-${index}`}>Edit</button>
                      <button onClick={() => handleDeletePayment(payment.id, index)} className="text-gray-400 hover:text-red-600 text-sm" data-testid={`invoice-payment-delete-${index}`}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
          })()}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Notes</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows="4"
                data-testid="invoice-notes"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Private Notes</label>
              <textarea
                value={formData.private_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, private_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-yellow-50"
                rows="3"
                data-testid="invoice-private-notes"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

      {showInvoiceSignatureWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="invoice-signature-warning-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-xl w-full my-auto sm:my-0">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800" data-testid="invoice-signature-warning-title">Signed Invoice Detected</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700" data-testid="invoice-signature-warning-text">
                If you are changing this invoice, you will be required to clear all signatures. If you do not wish to clear signatures, please duplicate this invoice into a new one.
              </p>
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
                <button
                  onClick={handleClearInvoiceSignaturesAndEdit}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold"
                  data-testid="invoice-signature-warning-clear"
                >
                  Yes, clear signatures and edit
                </button>
                <button
                  onClick={handleDuplicateInvoice}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  data-testid="invoice-signature-warning-duplicate"
                >
                  No, duplicate and edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showChangeOrderSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="change-order-send-modal">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg sm:max-w-6xl sm:h-[80vh] flex flex-col my-auto sm:my-0">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Send Change Order</h3>
              <button
                onClick={() => setShowChangeOrderSendModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="change-order-send-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 flex-1 flex flex-col overflow-hidden">
              <div className="text-xs text-gray-600" data-testid="change-order-send-summary">
                Change Order: {changeOrderToSend?.change_order_reason || changeOrderToSend?.description || 'Change Order'}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={changeOrderSendForm.recipient_email}
                  onChange={(e) => setChangeOrderSendForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="change-order-send-recipient"
                />
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={changeOrderSendForm.message}
                  onChange={(e) => setChangeOrderSendForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg flex-1 resize-none"
                  data-testid="change-order-send-message"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowChangeOrderSendModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                data-testid="change-order-send-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSendChangeOrder}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                data-testid="change-order-send-submit"
                disabled={changeOrderSending}
              >
                {changeOrderSending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="invoice-send-modal">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg sm:max-w-6xl sm:h-[80vh] flex flex-col my-auto sm:my-0">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">
                {sendForm.email_type === 'receipt' ? 'Send Receipt' : 'Send Invoice'}
              </h3>
              <button
                onClick={() => setShowSendModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="invoice-send-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 flex-1 flex flex-col overflow-hidden">
              {/* Invoice / Receipt toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden self-start" data-testid="invoice-send-type-toggle">
                <button
                  onClick={() => setSendForm(prev => ({ ...prev, email_type: 'invoice' }))}
                  className={`px-5 py-1.5 text-sm font-medium transition-colors ${sendForm.email_type === 'invoice' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="invoice-send-type-invoice"
                >
                  Invoice
                </button>
                <button
                  onClick={() => setSendForm(prev => ({ ...prev, email_type: 'receipt' }))}
                  className={`px-5 py-1.5 text-sm font-medium transition-colors ${sendForm.email_type === 'receipt' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="invoice-send-type-receipt"
                >
                  Receipt
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={sendForm.recipient_email}
                  onChange={(e) => setSendForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="invoice-send-recipient"
                />
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg flex-1 resize-none"
                  data-testid="invoice-send-message"
                />
              </div>
              <div className="mt-1 flex items-center gap-3 text-sm text-gray-700" data-testid="invoice-send-attach-pdf">
                <ToggleSwitch
                  checked={sendForm.attach_pdf}
                  onChange={(checked) => setSendForm(prev => ({ ...prev, attach_pdf: checked }))}
                  size="small"
                />
                <span>Attach PDF (opening the PDF attachment won't trigger link-open tracking)</span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-sm text-gray-700" data-testid="invoice-send-cc-admin">
                <ToggleSwitch
                  checked={sendForm.cc_admin}
                  onChange={(checked) => setSendForm(prev => ({ ...prev, cc_admin: checked }))}
                  size="small"
                />
                <span>Send copy to {salesSettings?.company_email || 'admin'}</span>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                  data-testid="invoice-send-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvoice}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  data-testid="invoice-send-confirm"
                >
                  {sendForm.email_type === 'receipt' ? 'Send Receipt' : 'Send Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {previewInvoiceToken && (
        <HtmlPreviewModal
          title={`Invoice Preview #${previewInvoiceNumber || ''}`}
          url={`${window.location.origin}/invoice/${previewInvoiceToken}?t=${previewInvoiceNonce}&mode=preview`}
          onClose={handleCloseInvoicePreview}
          testId="invoice-preview-modal"
        />
      )}

      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="invoice-payment-schedule-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Payment Schedule</h3>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Type Toggle — Joist-style radio buttons */}
              <div className="flex items-center gap-6" data-testid="invoice-payment-schedule-type">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="schedule_type"
                    checked={scheduleType === 'percent'}
                    onChange={() => setFormData(prev => ({ ...prev, payment_schedule_type: 'percent' }))}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid="invoice-payment-schedule-type-percent"
                  />
                  <span className="text-sm font-medium text-gray-700">%</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="schedule_type"
                    checked={scheduleType === 'amount'}
                    onChange={() => setFormData(prev => ({ ...prev, payment_schedule_type: 'amount' }))}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid="invoice-payment-schedule-type-amount"
                  />
                  <span className="text-sm font-medium text-gray-700">$</span>
                </label>
              </div>

              {/* Column Headers */}
              <div className="grid grid-cols-[1fr_110px_36px] gap-2 text-xs font-medium text-gray-400 pb-1" data-testid="invoice-payment-schedule-header">
                <span>Payment Name</span>
                <span>Payment Amount</span>
                <span />
              </div>

              {/* Schedule Rows */}
              <div className="space-y-2" data-testid="invoice-payment-schedule-rows">
                {scheduleItems.length === 0 && (
                  <div className="text-sm text-gray-400 py-4 text-center" data-testid="invoice-payment-schedule-empty-modal">
                    No schedule entries yet. Click "+ Add Payment" to start.
                  </div>
                )}
                {scheduleItems.map((item, index) => (
                  <div key={item.id || index} className="grid grid-cols-[1fr_110px_36px] gap-2 items-center" data-testid={`invoice-payment-schedule-row-${index}`}>
                    <input
                      type="text"
                      value={item.name || ''}
                      onChange={(e) => updateScheduleItem(index, 'name', e.target.value)}
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder={index === 0 ? 'Deposit' : `Payment ${index + 1}`}
                      data-testid={`invoice-payment-schedule-name-${index}`}
                    />
                    <div className="relative">
                      <input
                        type="number"
                        value={Number(item.amount || 0)}
                        onChange={(e) => updateScheduleItem(index, 'amount', e.target.value)}
                        className="w-full py-2.5 pl-3 pr-7 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        step={scheduleType === 'percent' ? '1' : '0.01'}
                        min="0"
                        placeholder="0"
                        data-testid={`invoice-payment-schedule-amount-${index}`}
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                        {scheduleType === 'percent' ? '%' : '$'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteScheduleItem(index)}
                      className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      data-testid={`invoice-payment-schedule-delete-${index}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* Add row */}
              <button
                type="button"
                onClick={addScheduleItem}
                className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                data-testid="invoice-payment-schedule-add"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Payment
              </button>

              {/* Total indicator */}
              {scheduleItems.length > 0 && (
                <div className={`text-xs font-semibold text-right ${scheduleRemaining < 0 ? 'text-red-500' : scheduleRemaining === 0 ? 'text-emerald-600' : 'text-gray-500'}`} data-testid="invoice-payment-schedule-remaining">
                  {scheduleRemainingLabel}
                </div>
              )}            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-4 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  if (scheduleSnapshotRef.current) {
                    invoiceAutoSaveBlockUntilRef.current = Date.now() + 3000;
                    setFormData(prev => ({ ...prev, ...scheduleSnapshotRef.current }));
                    scheduleSnapshotRef.current = null;
                  }
                  setShowScheduleModal(false);
                }}
                className="text-sm font-semibold text-gray-500 hover:text-gray-700 uppercase tracking-wide"
                data-testid="invoice-payment-schedule-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const invoiceId = getInvoiceId();
                  if (invoiceId) {
                    try {
                      invoiceAutoSaveBlockUntilRef.current = Date.now() + 5000;
                      const response = await axios.put(`${API}/sales/invoices/${invoiceId}`, {
                        payment_schedule: formData.payment_schedule,
                        payment_schedule_type: formData.payment_schedule_type
                      });
                      applyInvoiceUpdate(response.data, { refreshPreview: false });
                    } catch (err) {
                      console.error('Error saving payment schedule:', err);
                    }
                  }
                  scheduleSnapshotRef.current = null;
                  setShowScheduleModal(false);
                }}
                className="text-sm font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-wide"
                data-testid="invoice-payment-schedule-done"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="invoice-record-payment-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full my-auto sm:my-0">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Record Payment</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="invoice-record-payment-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* PAID / DUE progress bar */}
              <div data-testid="invoice-record-payment-progress">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-semibold text-emerald-600">PAID ${formatCurrency(formData.amount_paid)}</span>
                  <span className="font-semibold text-gray-700">DUE ${formatCurrency(formData.balance_due)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${formData.total > 0 ? Math.min(100, (formData.amount_paid / formData.total) * 100) : 0}%` }} />
                </div>
              </div>

              {/* Amount with Full button */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Payment</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                      placeholder="0.00"
                      step="0.01"
                      data-testid="invoice-record-payment-amount"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setPaymentForm(prev => ({ ...prev, amount: formData.balance_due.toFixed(2) }))}
                    className={`px-4 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                      parseFloat(paymentForm.amount) === formData.balance_due
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                    data-testid="invoice-record-payment-pay-full"
                  >
                    Full
                  </button>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.paid_date}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, paid_date: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  data-testid="invoice-record-payment-date"
                />
              </div>

              {/* Method toggle buttons */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Payment Method</label>
                <div className="grid grid-cols-3 gap-2" data-testid="invoice-record-payment-method-buttons">
                  {['Cash', 'Cheque', 'Credit Card', 'E-Transfer', 'Debit', 'Other'].map(method => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setPaymentForm(prev => {
                        const isCreditCard = method === 'Credit Card';
                        if (isCreditCard) {
                          // Auto-enable CC fee with invoice-level default when Credit Card is selected
                          const defaultValue = formData.cc_fee_value || prev.cc_fee_value || 0;
                          const defaultType = formData.cc_fee_type || prev.cc_fee_type || 'percent';
                          return {
                            ...prev,
                            method,
                            cc_fee_enabled: true,
                            cc_fee_type: defaultType,
                            cc_fee_value: defaultValue
                          };
                        }
                        return { ...prev, method };
                      })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        paymentForm.method === method
                          ? 'bg-gray-900 border-gray-900 text-white'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                      data-testid={`invoice-record-payment-method-${method.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Notes</label>
                <input
                  type="text"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  placeholder="Optional"
                  data-testid="invoice-record-payment-notes"
                />
              </div>

              {/* CC Processing Fee */}
              <div className={`rounded-lg p-3 border transition-colors ${paymentForm.cc_fee_enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`} data-testid="invoice-record-payment-cc-fee-section">
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentForm.cc_fee_enabled || false}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, cc_fee_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-blue-600"
                      data-testid="invoice-record-payment-cc-fee-toggle"
                    />
                    <span className="text-sm font-medium text-gray-700">CC Processing Fee</span>
                  </label>
                  <div className="flex items-center gap-1.5">
                    <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                      <button type="button" onClick={() => setPaymentForm(prev => ({ ...prev, cc_fee_type: 'percent' }))} className={`px-2 py-1 transition-colors ${(paymentForm.cc_fee_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="invoice-record-payment-cc-fee-type-percent">%</button>
                      <button type="button" onClick={() => setPaymentForm(prev => ({ ...prev, cc_fee_type: 'flat' }))} className={`px-2 py-1 transition-colors ${paymentForm.cc_fee_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="invoice-record-payment-cc-fee-type-flat">$</button>
                    </div>
                    <input
                      type="number"
                      value={paymentForm.cc_fee_value}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, cc_fee_value: parseFloat(e.target.value) || 0 }))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                      step="0.01"
                      data-testid="invoice-record-payment-cc-fee-value"
                    />
                  </div>
                </div>
                {paymentForm.cc_fee_enabled && parseFloat(paymentForm.amount || 0) > 0 && (() => {
                  const feeAmt = (paymentForm.cc_fee_type || 'percent') !== 'flat'
                    ? Math.round(parseFloat(paymentForm.amount || 0) * (paymentForm.cc_fee_value || 0) / 100 * 100) / 100
                    : parseFloat(paymentForm.cc_fee_value || 0);
                  return (
                    <div className="mt-2 text-xs text-blue-700 font-medium" data-testid="invoice-record-payment-cc-fee-preview">
                      Fee: +${formatCurrency(feeAmt)} &nbsp;·&nbsp; Total charged to card: ${formatCurrency(parseFloat(paymentForm.amount || 0) + feeAmt)}
                    </div>
                  );
                })()}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm"
                  data-testid="invoice-record-payment-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm"
                  data-testid="invoice-record-payment-submit"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEditPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="invoice-edit-payment-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full my-auto sm:my-0">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Edit Payment</h3>
              <button
                onClick={() => { setShowEditPaymentModal(false); setEditPaymentId(null); }}
                className="text-gray-500 hover:text-gray-700"
                data-testid="invoice-edit-payment-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  value={editPaymentForm.amount}
                  onChange={(e) => setEditPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                  step="0.01"
                  data-testid="invoice-edit-payment-amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                <select
                  value={editPaymentForm.method}
                  onChange={(e) => setEditPaymentForm(prev => ({ ...prev, method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="invoice-edit-payment-method"
                >
                  <option value="Manual">Manual</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="E-Transfer">E-Transfer</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit">Debit</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={editPaymentForm.paid_date}
                  onChange={(e) => setEditPaymentForm(prev => ({ ...prev, paid_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="invoice-edit-payment-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={editPaymentForm.notes}
                  onChange={(e) => setEditPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Optional"
                  data-testid="invoice-edit-payment-notes"
                />
              </div>

              {/* CC Processing Fee */}
              <div className={`rounded-lg p-3 border transition-colors ${editPaymentForm.cc_fee_enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`} data-testid="invoice-edit-payment-cc-fee-section">
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editPaymentForm.cc_fee_enabled || false}
                      onChange={(e) => setEditPaymentForm(prev => ({ ...prev, cc_fee_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-blue-600"
                      data-testid="invoice-edit-payment-cc-fee-toggle"
                    />
                    <span className="text-sm font-medium text-gray-700">CC Processing Fee</span>
                  </label>
                  <div className="flex items-center gap-1.5">
                    <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                      <button type="button" onClick={() => setEditPaymentForm(prev => ({ ...prev, cc_fee_type: 'percent' }))} className={`px-2 py-1 transition-colors ${(editPaymentForm.cc_fee_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="invoice-edit-payment-cc-fee-type-percent">%</button>
                      <button type="button" onClick={() => setEditPaymentForm(prev => ({ ...prev, cc_fee_type: 'flat' }))} className={`px-2 py-1 transition-colors ${editPaymentForm.cc_fee_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="invoice-edit-payment-cc-fee-type-flat">$</button>
                    </div>
                    <input
                      type="number"
                      value={editPaymentForm.cc_fee_value}
                      onChange={(e) => setEditPaymentForm(prev => ({ ...prev, cc_fee_value: parseFloat(e.target.value) || 0 }))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                      step="0.01"
                      data-testid="invoice-edit-payment-cc-fee-value"
                    />
                  </div>
                </div>
                {editPaymentForm.cc_fee_enabled && parseFloat(editPaymentForm.amount || 0) > 0 && (() => {
                  const feeAmt = (editPaymentForm.cc_fee_type || 'percent') !== 'flat'
                    ? Math.round(parseFloat(editPaymentForm.amount || 0) * (editPaymentForm.cc_fee_value || 0) / 100 * 100) / 100
                    : parseFloat(editPaymentForm.cc_fee_value || 0);
                  return (
                    <div className="mt-2 text-xs text-blue-700 font-medium" data-testid="invoice-edit-payment-cc-fee-preview">
                      Fee: +${formatCurrency(feeAmt)} &nbsp;·&nbsp; Total charged to card: ${formatCurrency(parseFloat(editPaymentForm.amount || 0) + feeAmt)}
                    </div>
                  );
                })()}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => { setShowEditPaymentModal(false); setEditPaymentId(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                  data-testid="invoice-edit-payment-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditPayment}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  data-testid="invoice-edit-payment-submit"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Version indicator - helps verify deployed code version */}
      <div className="text-center py-1 text-[10px] text-gray-300" data-testid="invoice-editor-version">v5</div>
    </div>
  );
};

export default InvoiceEditor;
